/** @jsxRuntime classic */
/** @jsx jsx */
// @ts-check
import { Box, Grid, Label, SemiTitle } from '@bottlebooks/gatsby-theme-base';
import { Plural } from '@lingui/macro';
import { graphql } from 'gatsby';
import { jsx } from 'theme-ui';
import BookAMeetingButton from './BookAMeetingButton';
import Contact from './Contact';

/**
 * Distribution contacts has 2 modes:
 * - on its own
 * - as part of a card
 * When it is part of a card, the SemiTitle becomes a label and the avatar isn't rendered.
 * @param {object} props
 * @param {object} props.brand
 * @param {'default' | 'card'} [props.variant="default"]
 * @returns
 */
export default function DistributionContacts({
  brand,
  variant = 'default',
  ...rest
}) {
  if (!hasContent(brand)) return null;
  const count = brand.distributionContacts.length;
  return (
    <Box {...rest}>
      {variant === 'default' ? (
        <SemiTitle sx={{ marginTop: 2 }}>
          <Plural
            value={count}
            one="Distribution Contact"
            other="Distribution Contacts"
          />
        </SemiTitle>
      ) : (
        <Label sx={{ marginTop: 2 }}>
          <Plural
            value={count}
            one="Distribution Contact"
            other="Distribution Contacts"
          />
        </Label>
      )}
      <Grid sx={{ marginTop: 2 }} width={300}>
        {brand.distributionContacts?.map((contact) => (
          <Contact
            key={contact.contactId}
            variant={variant === 'card' ? 'simple' : 'default'}
            contact={contact}
          >
            <BookAMeetingButton
              collectionId={brand.collectionId}
              contact={contact}
              exhibitor={{ exhibitorId: brand.companyId }}
              contactButtonVisibility="ENABLED"
              contactButtonAuthenticationMode="OPTIONAL"
              sx={{ marginTop: 2, backgroundColor: 'white' }}
            />
          </Contact>
        ))}
      </Grid>
    </Box>
  );
}

DistributionContacts.hasContent = hasContent;

function hasContent(brand) {
  if (!brand?.distributionContacts?.length) return false;
  return brand?.distributionContacts.some((contact) => {
    // If the brand is also the distribution contact with no further details,
    // we don't count it as having content.
    // We will render it though if there is more than a single contact.
    if (contact.companyId === brand.companyId && !contact.fullName) {
      return false;
    }
    return Contact.hasContent(contact);
  });
}

export const fragment = graphql`
  fragment bb_DistributionContacts on Bottlebooks_RegisteredBrand {
    companyId
    collectionId
    distributionContacts {
      contactId
      companyId
      ...bb_Contact
    }
  }
`;
